/**
 * ConfirmRemoveMemberModal Component
 * Confirmation dialog for removing organization members
 */
import { useState } from 'react';
import { MemberWithQuota } from '../../api/organizations';
import { XMarkIcon, ExclamationTriangleIcon } from './icons';
import Modal from '../common/Modal';

interface ConfirmRemoveMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: MemberWithQuota | null;
  organizationId: string;
  onConfirm: () => Promise<void>;
}

export default function ConfirmRemoveMemberModal({
  isOpen,
  onClose,
  member,
  onConfirm,
}: ConfirmRemoveMemberModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !member) return null;

  const handleClose = () => {
    setConfirmText('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (confirmText !== 'REMOVE') {
      setError('Please type "REMOVE" to confirm');
      return;
    }

    setLoading(true);

    try {
      await onConfirm();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const quota = member.quota;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="w-full max-w-md mx-4">
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-border">
          <h2 className="text-xl font-semibold text-white">Remove Member</h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-dark-text-muted hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Warning icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-yellow-500" />
              </div>
            </div>

            <p className="text-center text-dark-text">
              Are you sure you want to remove this member?
            </p>

            {/* Member info */}
            <div className="bg-dark-bg/30 border border-dark-border/50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-dark-bg/50 flex items-center justify-center text-dark-text font-semibold">
                  {member.member.full_name
                    ? member.member.full_name.charAt(0).toUpperCase()
                    : member.member.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-dark-text font-semibold">
                    {member.member.full_name || member.member.preferred_name || 'Unnamed User'}
                  </div>
                  <div className="text-sm text-dark-text-muted">{member.member.email}</div>
                </div>
              </div>
            </div>

            {/* Consequences */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-dark-text">This will:</p>
              <ul className="text-sm text-dark-text-muted space-y-2">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Revoke their access immediately</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Delete all their data (clipboard, conversations)</span>
                </li>
                {quota && (
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>
                      Free up their quota allocations:
                      <div className="ml-4 mt-1 space-y-0.5">
                        <div>- {quota.allocated_clipboard_slots} clipboard slots</div>
                        <div>- {quota.allocated_conversations} conversations</div>
                        <div>- {quota.allocated_ai_requests} AI requests</div>
                      </div>
                    </span>
                  </li>
                )}
              </ul>
            </div>

            {/* Warning */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-sm text-red-300 font-semibold">
                This action cannot be undone!
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* Confirmation input */}
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Type <strong>REMOVE</strong> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="REMOVE"
                className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl px-4 py-3
                         text-dark-text placeholder-gray-500
                         focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500
                         transition-all"
                autoComplete="off"
              />
            </div>
          </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-dark-border">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-3 text-white bg-dark-card border border-dark-border
                     rounded-lg hover:bg-dark-bg
                     transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || confirmText !== 'REMOVE'}
            className="flex-1 px-4 py-3 text-white bg-red-600 hover:bg-red-700
                     rounded-lg
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
          >
            {loading ? 'Removing...' : 'Remove Member'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
